import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Code, Music, Plane, Dumbbell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SubjectsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const SubjectsModal = ({ isOpen, onClose }: SubjectsModalProps) => {
  const navigate = useNavigate();

  const subjects = [
    {
      id: 'english',
      name: 'English',
      icon: BookOpen,
      description: 'Language learning, literature, and communication skills',
      route: '/subjects/english',
      color: 'bg-blue-100 text-blue-800 border-blue-200',
      iconColor: 'text-blue-600'
    },
    {
      id: 'programming',
      name: 'Programming',
      icon: Code,
      description: 'Web development, algorithms, and software engineering',
      route: '/subjects/programming',
      color: 'bg-green-100 text-green-800 border-green-200',
      iconColor: 'text-green-600'
    },
    {
      id: 'music',
      name: 'Music',
      icon: Music,
      description: 'Instruments, theory, composition, and performance',
      route: '/subjects/music',
      color: 'bg-purple-100 text-purple-800 border-purple-200',
      iconColor: 'text-purple-600'
    },
    {
      id: 'travel',
      name: 'Travel',
      icon: Plane,
      description: 'Language exchange, cultural guidance, and travel tips',
      route: '/subjects/travel',
      color: 'bg-orange-100 text-orange-800 border-orange-200',
      iconColor: 'text-orange-600'
    },
    {
      id: 'fitness',
      name: 'Fitness',
      icon: Dumbbell,
      description: 'Personal training, nutrition, and wellness coaching',
      route: '/subjects/fitness',
      color: 'bg-red-100 text-red-800 border-red-200',
      iconColor: 'text-red-600'
    }
  ];

  const handleSubjectClick = (route: string) => {
    navigate(route);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-sm sm:max-w-lg bg-white border border-gray-200 shadow-xl">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-lg sm:text-xl font-bold text-gray-900 text-center">
            Choose Your Subject
          </DialogTitle>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          {subjects.map((subject) => {
            const IconComponent = subject.icon;
            return (
              <Card
                key={subject.id}
                className="cursor-pointer hover:shadow-md transition-all duration-200 border hover:border-blue-300"
                onClick={() => handleSubjectClick(subject.route)}
              >
                <CardContent className="p-3 text-center space-y-2">
                  <div className="flex justify-center">
                    <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center">
                      <IconComponent className={`h-4 w-4 ${subject.iconColor}`} />
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1">
                      {subject.name}
                    </h3>
                    <Badge variant="secondary" className={`${subject.color} text-xs`}>
                      Find Tutors
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="flex justify-center mt-4">
          <Button
            variant="outline"
            onClick={onClose}
            className="px-6 text-sm"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubjectsModal;